import { useState, useEffect } from 'react';
import { useTranslate } from '@tolgee/react';
import styled from '@emotion/styled';
import { cx } from '@emotion/css';
import { Button, Tooltip, message, Drawer } from 'antd';
import { InfoCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import { OnOrder, subscriptionStatus } from 'services/api/pay';
import { useAuthorization } from 'components/Authorization';
import { useQuery } from 'components/RouterQuery';
import { useMemoizedFn } from 'ahooks';

import { usePackageV2 } from '../hooks/usePackage';
import PackageDlg from './PackageDlg';
import ZhixiaGift from './ZhixiaGift';
import { isQuickChat } from 'productLine';
import { useTransAppProductName } from 'hooks/useTrans';
import { PackageCard } from './PackageCard';
import {
  OpenEnterpriseTrafficOrderModalButton,
  OpenTrafficOrderModalButton,
} from './OrderAI/Button';
import { match } from 'ts-pattern';
import { useComboInfo, useIsEnterprisePkg, useOrderedPackagesActions } from '../hooks/useComboInfo';
import { filter } from 'lodash-es';
import AIServiceOverview, { AILightWeightServiceOverview } from './AIServiceOverview';
import ManageSubscriptionModal from './ManageSubscriptionModal';

export interface OrderDealProps {
  onOrder: OnOrder;
}

function OrderedDeal(props: OrderDealProps) {
  const { orderType: defaultOrderType, lightWeightSource = '' } = useQuery();
  const { onOrder } = props;
  const { t } = useTranslate();
  const appProductNameT = useTransAppProductName();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [orderType, setOrderType] = useState<Pay.OrderType>();
  const { payload } = useAuthorization<Oversea.Token.Payload>();
  const { orgId } = payload || {};

  const { loading: loadingUpgrade, fetch: fetchUpgradePackageList } = usePackageV2(
    orgId,
    'UPGRADE',
  );

  const { loading: loadingRenew, fetch: fetchRenewPackageList } = usePackageV2(orgId, 'RENEW');

  const [packageInfo, setPackageInfo] = useState<Pay.PackageInfoV2 | undefined>();
  const [upgradeSubscriptionCurrency, setUpgradeSubscriptionCurrency] =
    useState<General.Currency>();
  const [loadingSubscriptionCurrency, setLoadingSubscriptionCurrency] = useState(false);
  const orderedPackages = useComboInfo(true);
  const isEnterprisePkg = useIsEnterprisePkg();
  const [manageSubscriptionVisible, setManageSubscriptionVisible] = useState(false);

  const { canUpgrade, canRenew } = useOrderedPackagesActions();

  // 生效中的订阅套餐
  const workingSubscription = orderedPackages.find(
    (v) => (v.status === 1 || v.status === 0) && v.autoRenewing,
  );
  const activeSubscription = orderedPackages.find((v) => v.status === 1 && v.autoRenewing);
  const upgradeLoading = loadingUpgrade || loadingSubscriptionCurrency;

  // 触发升级套餐服务
  const triggerUpgradePackage = useMemoizedFn(() => {
    setLoadingSubscriptionCurrency(!!activeSubscription);
    Promise.all([
      fetchUpgradePackageList(),
      activeSubscription ? subscriptionStatus() : Promise.resolve(undefined),
    ])
      .then(([rst, subscription]) => {
        setUpgradeSubscriptionCurrency(subscription?.currency);
        setOrderType('UPGRADE');
        setPackageInfo(rst);
        setIsModalVisible(true);
      })
      .catch((e) => {
        message.error(`get_package_info UPGRADE ${e?.message}`);
      })
      .finally(() => setLoadingSubscriptionCurrency(false));
  });
  // 触发续订套餐服务
  const triggerRenewPackage = useMemoizedFn(() => {
    fetchRenewPackageList()
      .then((rst) => {
        setOrderType('RENEW');
        setPackageInfo(rst);
        setIsModalVisible(true);
      })
      .catch((e) => {
        message.error(`get_package_info UPGRADE ${e?.message}`);
      });
  });

  useEffect(() => {
    // 根据路由query orderType 决定是否触发套餐变更
    const defOrderType = defaultOrderType?.toUpperCase();
    if (canUpgrade && defOrderType === 'UPGRADE') {
      triggerUpgradePackage();
    }
    if (canRenew && defOrderType === 'RENEW') {
      triggerRenewPackage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultOrderType, canUpgrade, canRenew]);

  const titleMap: Record<Pay.TrafficPackageType, string> = {
    INCREASE_THE_MONTHLY_CAP: t('aiorder_type1_title'),
    TRAFFIC_PACKAGE: t('aiorder_type2_title'),
    ENTERPRISE_TRAFFIC_PACKAGE: t('aiorder_type3_title'),
  };

  const trafficPackageCardNode = (card = false) => {
    const trafficPkgs = filter(orderedPackages, (v) => v.productName === '流量包');
    if (trafficPkgs.length > 0 || true) {
      const cards = trafficPkgs.map((v) => {
        return (
          <PackageCard
            key={`${v.productName}-${v.validTimeEnd}`}
            title={titleMap[v.trafficPackageType!]}
            orderedPackage={v}
            color={v.trafficPackageType === 'INCREASE_THE_MONTHLY_CAP' ? 'purple' : 'green'}
            showHandler
          />
        );
      });
      return card ? (
        cards
      ) : (
        <div
          className={cx(
            'mt-4 rounded border border-solid border-fill-200',
            isQuickChat ? '!mt-0' : '',
          )}
        >
          <ClientBar>
            <span>{t('header.AI参与服务买家数')}</span>
            <div>
              <OpenTrafficOrderModalButton />
            </div>
          </ClientBar>
          <div className="px-[16px] pt-[12px]">
            <AIServiceOverview simple />
          </div>
          <PackageDeal>{cards}</PackageDeal>
        </div>
      );
    }
  };

  return orderedPackages ? (
    <div>
      <ZhixiaGift />
      {orderedPackages.find((v) => v.productName === '乐聊客服工作台') ? (
        <div
          className={cx('rounded border border-solid border-fill-200', isQuickChat ? '!mt-0' : '')}
        >
          <ClientBar>
            <span>{t(`packageType.1`)}</span>
            <ClientBarBtns>
              {workingSubscription && (
                <Button onClick={() => setManageSubscriptionVisible(true)}>
                  {t('管理自动续费')}
                </Button>
              )}
              <Tooltip title={!canUpgrade ? t('market.升级套餐提示') : ''}>
                <Button
                  disabled={upgradeLoading || !canUpgrade}
                  onClick={triggerUpgradePackage}
                  className="!mr-4"
                >
                  {t('market.升级套餐')} {upgradeLoading ? <LoadingOutlined /> : null}
                </Button>
              </Tooltip>
              <Tooltip title={!canRenew ? t('market.续订套餐提示') : ''}>
                <Button
                  disabled={loadingRenew || !canRenew}
                  onClick={triggerRenewPackage}
                  className="!mr-0"
                >
                  {t('market.续订套餐')} {loadingRenew ? <LoadingOutlined /> : null}
                </Button>
              </Tooltip>
            </ClientBarBtns>
          </ClientBar>
          <PackageDeal>
            {orderedPackages.map((v) => {
              return v.productName === '乐聊客服工作台' ? (
                <PackageCard
                  key={v.id}
                  title={t('packageName.乐聊客服工作台')}
                  orderedPackage={v}
                  color="orange"
                />
              ) : null;
            })}
          </PackageDeal>
        </div>
      ) : null}

      {orderedPackages.find((v) => v.productName === 'QikChat工作台') ? (
        <div
          className={cx(
            'mt-4 rounded border border-solid border-fill-200',
            isQuickChat ? '!mt-0' : '',
          )}
        >
          <ClientBar>
            <span>{appProductNameT('quickchat_workbench')}</span>
            <ClientBarBtns>
              {workingSubscription && (
                <Button onClick={() => setManageSubscriptionVisible(true)}>
                  {t('管理自动续费')}
                </Button>
              )}
              <Tooltip title={!canUpgrade ? t('market.升级套餐提示') : ''}>
                <Button
                  disabled={upgradeLoading || !canUpgrade}
                  onClick={triggerUpgradePackage}
                  className="!mr-4"
                >
                  {t('market.升级套餐')} {upgradeLoading ? <LoadingOutlined /> : null}
                </Button>
              </Tooltip>
              <Tooltip title={!canRenew ? t('market.续订套餐提示') : ''}>
                <Button
                  disabled={loadingRenew || !canRenew}
                  onClick={triggerRenewPackage}
                  className="!mr-0"
                >
                  {t('market.续订套餐')} {loadingRenew ? <LoadingOutlined /> : null}
                </Button>
              </Tooltip>
            </ClientBarBtns>
          </ClientBar>
          <PackageDeal>
            {orderedPackages.map((v) => {
              return v.productName === 'QikChat工作台' ? (
                <PackageCard
                  key={v.id}
                  title={appProductNameT('quickchat_workbench')}
                  orderedPackage={v}
                  color="orange"
                />
              ) : null;
            })}
          </PackageDeal>
        </div>
      ) : null}

      {orderedPackages.find((v) => v.productName === '乐聊高级客服') ? (
        <div
          className={cx('rounded border border-solid border-fill-200', isQuickChat ? '!mt-0' : '')}
        >
          <ClientBar>
            <span>
              {t('packageType.2')}
              <Tooltip placement="top" title={t('market.乐聊高级客服提示')}>
                <InfoCircleOutlined />
              </Tooltip>
            </span>
            <OpenEnterpriseTrafficOrderModalButton />
          </ClientBar>
          <PackageDeal>
            {orderedPackages.map((v) => {
              return match(v)
                .with({ productName: '乐聊高级客服' }, () => (
                  <PackageCard
                    key={v.id}
                    title={t('packageName.高级智能客服机器人')}
                    orderedPackage={v}
                    color="blue"
                    showHandler
                  />
                ))
                .otherwise(() => null);
            })}
            {trafficPackageCardNode(true)}
          </PackageDeal>
        </div>
      ) : null}

      {!isEnterprisePkg && (
        <AILightWeightServiceOverview
          source={`serviceInfo${lightWeightSource ? '-' + lightWeightSource : ''}`}
        />
      )}
      {!isEnterprisePkg && trafficPackageCardNode()}

      <ManageSubscriptionModal
        visible={manageSubscriptionVisible}
        orderedPackage={workingSubscription}
        onCancel={() => setManageSubscriptionVisible(false)}
      />
      <DrawerModal
        title={orderType === 'UPGRADE' ? t('market.升级套餐') : t('market.续订套餐')}
        visible={isModalVisible}
        width={890}
        footer={null}
        destroyOnClose
        // onOk={handleOk}
        onClose={() => {
          setIsModalVisible(false);
        }}
      >
        {packageInfo && orderedPackages && orgId && orderType ? (
          <PackageDlg
            key={orderType}
            orderType={orderType}
            orgId={orgId}
            orderedPackages={orderedPackages}
            packageInfo={packageInfo}
            lockedCurrency={orderType === 'UPGRADE' ? upgradeSubscriptionCurrency : undefined}
            onOrder={onOrder}
            onSuccess={() => setIsModalVisible(false)}
          />
        ) : null}
      </DrawerModal>
    </div>
  ) : null;
}

export default OrderedDeal;

const ClientBar = styled.div`
  height: 48px;
  background: var(--fett-erp-card-bg);
  display: flex;
  padding: 0 16px;
  align-items: center;
  justify-content: space-between;
  font-weight: 500;
  & > span {
    color: var(--fett-text-primary);
    span.anticon {
      color: var(--fett-text-secondary);
      margin-left: 4px;
    }
  }
`;
const ClientBarBtns = styled.div`
  button:first-of-type {
    margin-right: 16px;
  }
`;
const PackageDeal = styled.div`
  display: flex;
  flex-direction: row;
  padding: 16px;
  padding-bottom: 0;
  flex-wrap: wrap;
  > div:not(:last-child) {
    margin-right: 18px;
  }
`;
const DrawerModal = styled(Drawer)`
  .ant-drawer-content-wrapper {
    width: 78% !important;
    min-width: 800px;
  }
`;
